//TODO: Implement loggig and error handling

import { ChatRequest, OrchestrationState, PromptConfig, UserInfo, FAQItem, GlossaryItem } from "../common/models";
import { getUnifiedAccessToken } from "../auth/getAccessToken";
import { DeviceUtils } from '../utils/deviceUtils';
import { DownloadNotificationService } from './downloadNotificationService';

// TypeScript declarations for File System Access API
interface FileSystemFileHandle {
  createWritable(): Promise<FileSystemWritableFileStream>;
}

interface FileSystemWritableFileStream {
  write(data: any): Promise<void>;
  close(): Promise<void>;
}

declare global {
  interface Window {
    showSaveFilePicker(options?: {
      suggestedName?: string;
      types?: Array<{
        description: string;
        accept: Record<string, string[]>;
      }>;
    }): Promise<FileSystemFileHandle>;
  }
}

// services/ApiService.ts
export class ApiService {
  private baseUrl: string;
  private microsoftAuthScope: string // Add this
  sharedHeaders: Record<string, string | number> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  }

  constructor(baseUrl: string, microsoftAuthScope: string) {
    this.baseUrl = baseUrl;
    this.microsoftAuthScope = microsoftAuthScope;
  }

  setToken = (token: string) => {
    this.sharedHeaders['Authorization'] = `Bearer ${token}`
  }

  async approveMe(): Promise<{ message: string }> {
    try {
      return this.postJson<{}, { message: string }>("/api/admin/approve/me", {}, new AbortController().signal);
    } catch (error) {
      throw error;
    }
  }

  // New method to check approval status
  async checkApprovalStatus(): Promise<{ isApproved: boolean }> {
    try {
      return this.get<{ isApproved: boolean }>("/api/admin/check-approval", new AbortController().signal);
    } catch (error) {

      throw error;
    }
  }

  async checkAdminStatus(): Promise<{ isAdmin: boolean }> {
    try {
      return this.get<{ isAdmin: boolean }>("/api/admin/is-admin", new AbortController().signal);
    } catch (error) {

      throw error;
    }
  }
  // Check developer status
  async checkDeveloperStatus(): Promise<{ isDeveloper: boolean }> {
    try {
      return this.get<{ isDeveloper: boolean }>("/api/admin/is-developer", new AbortController().signal);
    } catch (error) {

      throw error;
    }
  }
  // Fetch unassigned users
  async getUnassignedUsers(): Promise<UserInfo[]> {
    return this.get<UserInfo[]>("/api/admin/users-not-in-group/Approved", new AbortController().signal);
  }

  // Fetch approved users
  async getApprovedUsers(): Promise<UserInfo[]> {
    return this.get<UserInfo[]>("/api/admin/users-in-group/Approved", new AbortController().signal);
  }

  // Approve a user (adds to both Approved and Internal groups)
  async approveUser(userId: string): Promise<void> {
    return this.postJson<{ userId: string }, void>(
      "/api/admin/approve-user",
      { userId },
      new AbortController().signal
    );
  }

  // Remove a user from the "Approved" group
  async removeUser(userId: string): Promise<void> {
    return this.postJson<{ userId: string; groupName: string }, void>(
      "/api/admin/remove-user-from-group",
      { userId, groupName: "Approved" },
      new AbortController().signal
    );
  }
  // Fetch prompts
  async getPrompts(abortSignal: AbortSignal): Promise<PromptConfig[]> {
    return this.get<PromptConfig[]>("/api/prompts", abortSignal);
  }

  // Save a new prompt
  async savePrompt(prompt: PromptConfig, abortSignal: AbortSignal): Promise<PromptConfig> {
    return this.postJson<PromptConfig, PromptConfig>("/api/prompts", prompt, abortSignal);
  }
  // Save a new prompt
  async updatePrompt(prompt: PromptConfig, abortSignal: AbortSignal): Promise<PromptConfig> {
    return this.put2<PromptConfig, PromptConfig>("/api/prompts", abortSignal, prompt);
  }
  async deletePrompt(prompt: PromptConfig, abortSignal: AbortSignal): Promise<void> {
    this.delete<PromptConfig, PromptConfig>("/api/prompts", prompt, abortSignal);
  }
  // Save or update a prompt
  async saveOrUpdatePrompt(prompt: PromptConfig): Promise<PromptConfig> {
    const endpoint = `/api/prompts/${prompt.version ? `update/${prompt.version}` : ""}`;
    const method = prompt.version ? "PATCH" : "POST";

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method,
        headers: {
          "Content-Type": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify(prompt),
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        console.error(`API POST returned ${response.status}:`, text);
        throw new Error(`HTTP error! Status: ${response.status} ${text}`);
      }
      return await response.json();
    } catch (error) {
      console.error(`API ${method} request failed:`, error);
      throw error;
    }
  }

  // Initiate a prompt update
  async initiatePrompt(prompt: PromptConfig, abortSignal: AbortSignal): Promise<PromptConfig> {
    const endpoint = `/api/prompts/initiate`;

    return this.postJson<PromptConfig, PromptConfig>(endpoint, prompt, abortSignal);
  }

  // Upload playground documents
  async uploadPlaygroundDocuments(promptTitle: string, files: File[]): Promise<{ documentUrls: string[] }> {
    const formData = new FormData();
    formData.append('promptTitle', promptTitle);
    
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Remove Content-Type from headers to let browser set it with boundary for multipart/form-data
    const headers = { ...this.sharedHeaders };
    delete headers['Content-Type'];
    delete headers['Accept'];

    const response = await fetch(`${this.baseUrl}/api/prompts/documents/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.error(`Upload documents failed with ${response.status}:`, text);
      throw new Error(`HTTP error! Status: ${response.status} ${text}`);
    }

    return await response.json();
  }

  // Delete playground documents
  async deletePlaygroundDocuments(promptTitle: string, documentUrls: string[]): Promise<{ message: string; deletedCount: number; skippedCount: number }> {
    const response = await fetch(`${this.baseUrl}/api/prompts/documents/delete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ promptTitle, documentUrls }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.error(`Delete documents failed with ${response.status}:`, text);
      throw new Error(`HTTP error! Status: ${response.status} ${text}`);
    }

    return await response.json();
  }
  
  // Preload playground documents into Kernel Memory
  async preloadPlaygroundDocuments(documentUrls: string[]): Promise<{ documentId: string; processedCount: number }> {
    const response = await fetch(`${this.baseUrl}/api/prompts/documents/preload`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ documentUrls }),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      console.error(`Preload documents failed with ${response.status}:`, text);
      throw new Error(`HTTP error! Status: ${response.status} ${text}`);
    }

    return await response.json();
  }

  // Generic GET method
  async get<T>(endpoint: string, abortSignal: AbortSignal): Promise<T> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "GET",
        headers: {
          ...(this.sharedHeaders as Record<string, string>)
        },
        credentials: 'include', // Sends accessToken cookie
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return Promise.reject(error);
      }
      console.error("API GET request failed:", error);
      throw error;
    }
  }
  
  async exportChat(exportType: string, sessionId: string, abortSignal: AbortSignal): Promise<void> {
    // Log device info for debugging
    const deviceInfo = DeviceUtils.getDeviceInfo();

    // Use the new generate-link endpoint that returns JSON with SAS URL
    const makeRequest = async (): Promise<Response> => {
      return await fetch(`${this.baseUrl}/api/enhanced-export/generate-link/${exportType}/${sessionId}`, {
        method: "GET",
        headers: {
          "Accept": "application/json"
        },
        credentials: 'include',
        signal: abortSignal,
      });
    };

    let response = await makeRequest();

    // Retry once if we get 401 (similar to getCurrentUser logic)
    if (response.status === 401) {
      console.log("Export request returned 401, retrying once...");
      await new Promise(resolve => setTimeout(resolve, 500));
      response = await makeRequest();
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Parse JSON response containing download link
    const exportData: {
      downloadUrl: string;
      fileName: string;
      expiresAt: string;
      blobPath: string;
      fileSizeBytes: number;
    } = await response.json();

    console.log("Export link generated:", {
      fileName: exportData.fileName,
      size: exportData.fileSizeBytes,
      expiresAt: exportData.expiresAt,
      blobPath: exportData.blobPath
    });

    // Import and use DownloadNotificationService to show notification with link
    const { DownloadNotificationService } = await import('./downloadNotificationService');
    DownloadNotificationService.showDownloadReady(
      exportData.fileName,
      exportData.downloadUrl,
      exportData.blobPath,
      exportData.expiresAt
    );

    // Trigger automatic download
    const { DownloadService } = await import('./downloadService');
    DownloadService.triggerDownload(exportData.downloadUrl, exportData.fileName);
  }

  async regenerateExportLink(blobPath: string, abortSignal: AbortSignal): Promise<{
    downloadUrl: string;
    fileName: string;
    expiresAt: string;
    blobPath: string;
    fileSizeBytes: number;
  }> {
    const makeRequest = async (): Promise<Response> => {
      return await fetch(`${this.baseUrl}/api/enhanced-export/regenerate-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify({ blobPath }),
        signal: abortSignal,
      });
    };

    let response = await makeRequest();

    // Retry once if we get 401
    if (response.status === 401) {
      console.log("Regenerate link request returned 401, retrying once...");
      await new Promise(resolve => setTimeout(resolve, 500));
      response = await makeRequest();
    }

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error("Export file not found or has been deleted");
      }
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.json();
  }

  async previewExport(exportType: string, sessionId: string, abortSignal: AbortSignal): Promise<string> {
    // Use the new enhanced export preview endpoint
    const response = await fetch(`${this.baseUrl}/api/enhanced-export/preview/${exportType}/${sessionId}`, {
      method: "GET",
      headers: {
        "Accept": "text/html"
      },
      credentials: 'include',
      signal: abortSignal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return await response.text();
  }

  async exportBattleCard(sections: Array<{ agentName: string; title: string; content: string }>, sessionId: string, title: string = "Battle Card", abortSignal: AbortSignal, options?: { singlePageRestriction?: boolean; allowTruncation?: boolean }): Promise<void> {
    const requestBody = {
      sessionId,
      title,
      sections,
      ...(options && { options })
    };

    const makeRequest = async (): Promise<Response> => {
      return await fetch(`${this.baseUrl}/api/enhanced-export/battle-card/generate-link`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
        signal: abortSignal,
      });
    };

    let response = await makeRequest();

    // Retry once if we get 401
    if (response.status === 401) {
      console.log("Battle Card export request returned 401, retrying once...");
      await new Promise(resolve => setTimeout(resolve, 500));
      response = await makeRequest();
    }

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    // Parse JSON response containing download link
    const exportData: {
      downloadUrl: string;
      fileName: string;
      expiresAt: string;
      blobPath: string;
      fileSizeBytes: number;
    } = await response.json();

    console.log("Battle Card export link generated:", {
      fileName: exportData.fileName,
      size: exportData.fileSizeBytes,
      expiresAt: exportData.expiresAt,
      blobPath: exportData.blobPath
    });

    // Import and use DownloadNotificationService to show notification with link
    const { DownloadNotificationService } = await import('./downloadNotificationService');
    DownloadNotificationService.showDownloadReady(
      exportData.fileName,
      exportData.downloadUrl,
      exportData.blobPath,
      exportData.expiresAt
    );

    // Trigger automatic download
    const { DownloadService } = await import('./downloadService');
    DownloadService.triggerDownload(exportData.downloadUrl, exportData.fileName);
  }

  // Update the downloadFileModern method to handle the user gesture requirement
  private async downloadFileModern(blob: Blob, filename: string): Promise<void> {
    try {
      // Check if we're in a user gesture context
      if (!window.isSecureContext) {
        throw new Error('Not in secure context');
      }

      // Try the modern API first
      const fileHandle = await window.showSaveFilePicker({
        suggestedName: filename,
        types: [{
          description: 'PDF files',
          accept: { 'application/pdf': ['.pdf'] }
        }]
      });

      const writable = await fileHandle.createWritable();
      await writable.write(blob);
      await writable.close();
    } catch (error) {
      // If modern API fails, fall back to traditional download
      this.downloadFileFallback(blob, filename);
    }
  }

  // Add a fallback method for traditional downloads
  private downloadFileFallback(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Update the main downloadFile method
  private async downloadFile(blob: Blob, filename: string): Promise<void> {
    // Check if the File System Access API is supported
    if ('showSaveFilePicker' in window) {
      try {
        await this.downloadFileModern(blob, filename);
        return;
      } catch (error) {

      }
    }

    // Fallback to traditional download
    this.downloadFileFallback(blob, filename);
  }



  private async downloadFileMobileOptimized(blob: Blob, fileName: string): Promise<void> {
    // Try the direct download approach first
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = fileName;
    link.style.display = 'none';

    // Set additional attributes for better mobile support
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener noreferrer');

    document.body.appendChild(link);

    return new Promise((resolve, reject) => {
      try {
        // Create a more reliable click event
        const event = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: 1
        });

        link.dispatchEvent(event);

        // Check if download started by monitoring the blob URL
        setTimeout(() => {
          document.body.removeChild(link);

          // For mobile emulation, the file should appear in downloads
          DownloadNotificationService.success(
            'Download Started',
            `${fileName} is downloading to your device.`
          );

          // Clean up after a delay
          setTimeout(() => URL.revokeObjectURL(url), 2000);
          resolve();
        }, 500);

      } catch (error) {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        // Fallback: try alternative download method
        this.downloadFileMobileFallback(blob, fileName)
          .then(resolve)
          .catch(reject);
      }
    });
  }

  private async downloadFileMobileFallback(blob: Blob, fileName: string): Promise<void> {
    // Alternative approach: create a temporary download link with proper MIME handling
    const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));

    try {
      // For mobile browsers, sometimes opening with specific intent works better
      const isSafari = DeviceUtils.isIOSSafari();

      if (isSafari) {
        // Safari-specific handling - matches real iPhone experience
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.target = '_blank';

        // For Safari, we need to trigger in response to user gesture
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        DownloadNotificationService.info(
          'PDF Opened in New Tab',
          'To save: Tap the middle button (Share button) at the bottom and select "Save to Files"',
          DeviceUtils.getDownloadInstructions()
        );
      } else {
        // For other mobile browsers, open in new tab with instructions
        const newWindow = window.open(url, '_blank');

        if (newWindow) {
          DownloadNotificationService.info(
            'PDF Ready',
            `PDF opened. To save as "${fileName}": tap the download icon or use your browser's share menu.`,
            DeviceUtils.getDownloadInstructions()
          );
        } else {
          throw new Error('Popup blocked');
        }
      }
    } finally {
      // Clean up URL after delay
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    }
  }

  private handleDownloadError(error: any, fileName: string): void {


    if (error.message?.includes('Popup blocked') || error.message?.includes('blocked')) {
      DownloadNotificationService.error(
        'Download Blocked',
        'Download blocked by browser. Please allow pop-ups and try again.'
      );
    } else {
      DownloadNotificationService.error(
        'Download Failed',
        `Failed to download ${fileName}. Please try again or contact support.`
      );
    }
  }



  private downloadFileStandard(blob: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // Show success notification
    DownloadNotificationService.success(
      'Download Started',
      `${fileName} is downloading to your device.`,
      fileName
    );

    // Clean up
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 100);
  }

  private downloadFileIOS(blob: Blob, fileName: string): void {
    // For iOS, create a shareable link that opens the PDF
    const url = window.URL.createObjectURL(blob);

    // Create a link that opens in a new tab
    const a = document.createElement("a");
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.style.display = 'none';
    a.download = fileName; // Set the filename for iOS
    document.body.appendChild(a);
    a.click();

    // Provide user instructions using the notification service
    setTimeout(() => {
      const instructions = DeviceUtils.getDownloadInstructions();
      DownloadNotificationService.info(
        'PDF Ready for Download',
        `${fileName} has been opened in a new tab.`,
        instructions
      );

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 1000);
  }

  private downloadFileAndroid(blob: Blob, fileName: string): void {
    // For Android, try direct download first, then fallback
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.style.display = 'none';
    document.body.appendChild(a);

    // Add event listeners to detect if download worked
    let downloadStarted = false;

    const onFocus = () => {
      downloadStarted = true;
      window.removeEventListener('focus', onFocus);
    };

    window.addEventListener('focus', onFocus);

    a.click();

    // Check if download started after a delay
    setTimeout(() => {
      if (!downloadStarted) {
        // Download likely didn't work, open in new tab
        window.open(url, '_blank');
        const instructions = DeviceUtils.getDownloadInstructions();
        DownloadNotificationService.warning(
          'Download Assistance Required',
          `${fileName} has been opened in a new tab.`,
          instructions
        );
      } else {
        DownloadNotificationService.success(
          'Download Started',
          `${fileName} is downloading to your device.`,
          fileName
        );
      }

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      window.removeEventListener('focus', onFocus);
    }, 2000);
  }

  private downloadFileMobile(blob: Blob, fileName: string): void {
    // For other mobile browsers, create a blob URL and open it
    const url = window.URL.createObjectURL(blob);

    // Try to open in new window first
    const newWindow = window.open(url, '_blank');

    if (newWindow) {
      // Success - the file opened in a new tab
      setTimeout(() => {
        const instructions = DeviceUtils.getDownloadInstructions();
        DownloadNotificationService.info(
          'PDF Ready for Download',
          `${fileName} has been opened in a new tab.`,
          instructions
        );
        window.URL.revokeObjectURL(url);
      }, 1000);
    } else {
      // Popup blocked or other issue - fallback to location
      DownloadNotificationService.warning(
        'Popup Blocked',
        'Your browser blocked the popup. Click OK in the confirmation dialog to open the file.',
        'Use your browser\'s download or share options to save the file after opening.'
      );

      if (confirm('Download ready. Click OK to open the file in a new tab where you can save it.')) {
        window.location.href = url;
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
        }, 10000);
      }
    }
  }
  // Generic UPLOAD method
  async upload<TResponse>(
    endpoint: string,
    files: File[],
    abortSignal: AbortSignal
  ): Promise<TResponse> {
    try {

      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append("files", file); // use same key for all files
      });
      // Add file to formData
      // formData.append('file1', files[0], files[0].name);
      // formData.append('file2', files[1], files[1].name);

      // Add metadata (as stringified JSON if backend expects JSON structure)
      // formData.append('documentId', 'doc01');
      // formData.append('tags', JSON.stringify([
      //   'user:devis@contoso.com',
      //   'collection:business',
      //   'collection:plans',
      //   'fiscalYear:2025'
      // ]));

      /**
       * Do not specify content type, browser is smart enough to set it to multipart/form-data with a boundary
       */
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        credentials: 'include',
        body: formData,
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {

      throw error;
    }
  }

  // Generic POST method
  async post<TRequest, TResponse>(
    endpoint: string,
    data: TRequest,
    abortSignal: AbortSignal
  ): Promise<TResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain"
        },
        credentials: 'include',
        body: JSON.stringify(data),
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("API POST request failed:", error);
      throw error;
    }
  }
  async postJson<TRequest, TResponse>(
    endpoint: string,
    data: TRequest,
    abortSignal: AbortSignal
  ): Promise<TResponse> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify(data),
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error("API POST request failed:", error);
      throw error;
    }
  }

  // POST method for FormData
  async postFormData(
    endpoint: string,
    formData: FormData,
    abortSignal: AbortSignal
  ): Promise<Response> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "POST",
        credentials: 'include',
        body: formData,
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return response;
    } catch (error) {
      console.error("API POST FormData request failed:", error);
      throw error;
    }
  }
  
  async put<TRequest, TResponse>(
    endpoint: string,
    abortSignal: AbortSignal
  ): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "PUT",
        headers: {
          "Content-Type": "text/plain"
        },
        credentials: 'include',
        body: '',
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // return no content
      return Promise.resolve();
    } catch (error) {
      console.error("API PUT request failed:", error);
      throw error;
    }
  }
  async put2<TRequest, TResponse>(
    endpoint: string,
    abortSignal: AbortSignal,
    data?: TRequest
  ): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: 'include',
        body: data ? JSON.stringify(data) : '',
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      // return no content
      return Promise.resolve();
    } catch (error) {
      console.error("API PUT request failed:", error);
      throw error;
    }
  }
  async stream(
    requestData: ChatRequest,
    abortSignal: AbortSignal,
    messageId?: string
  ): Promise<Response> {
    const url = `${this.baseUrl}/api/streaming${messageId ? `/update/${messageId}` : ''}`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: 'include',
      body: JSON.stringify(requestData),
      signal: abortSignal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Server error! Status: ${response.status}`);
        }
        return response;
      })
      .catch((error) => {
        console.error("API POST request failed:", error);
        throw error;
      });

    return response;
  }
  async getOrchState(sessionId: string): Promise<OrchestrationState | null> {
    const response = await fetch(`${this.baseUrl}/api/currentAgent/${sessionId}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      },
      credentials: 'include'
    });

    if (response.ok) {
      const data = await response.json();
      return data as OrchestrationState;
    }
    return null;
  }
  async delete<TRequest, TResponse>(
    endpoint: string,
    data: TRequest,
    abortSignal: AbortSignal
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: 'include',
        body: JSON.stringify(data),
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
    } catch (error) {
      console.error("API POST request failed:", error);
      throw error;
    }
  }

  // FAQ API
  async getFAQs(abortSignal: AbortSignal): Promise<FAQItem[]> {
    return this.get<FAQItem[]>("/api/faqs", abortSignal);
  }
  async addFAQ(faq: FAQItem, abortSignal: AbortSignal): Promise<FAQItem> {
    return this.postJson<FAQItem, FAQItem>("/api/faqs", faq, abortSignal);
  }
  async updateFAQ(faq: FAQItem, abortSignal: AbortSignal): Promise<FAQItem> {
    return this.put2<FAQItem, FAQItem>("/api/faqs", abortSignal, faq);
  }
  async deleteFAQ(faq: FAQItem, abortSignal: AbortSignal): Promise<void> {
    return this.delete<FAQItem, void>("/api/faqs", faq, abortSignal);
  }

  // Glossary API
  async getGlossary(abortSignal: AbortSignal): Promise<GlossaryItem[]> {
    return this.get<GlossaryItem[]>("/api/glossary", abortSignal);
  }
  async addGlossary(item: GlossaryItem, abortSignal: AbortSignal): Promise<GlossaryItem> {
    return this.postJson<GlossaryItem, GlossaryItem>("/api/glossary", item, abortSignal);
  }
  async updateGlossary(item: GlossaryItem, abortSignal: AbortSignal): Promise<GlossaryItem> {
    return this.put2<GlossaryItem, GlossaryItem>("/api/glossary", abortSignal, item);
  }
  async deleteGlossary(item: GlossaryItem, abortSignal: AbortSignal): Promise<void> {
    return this.delete<GlossaryItem, void>("/api/glossary", item, abortSignal);
  }
  //subscriptions - method implemented below


  async getOverride<T>(endpoint: String, abortSignal: AbortSignal, token?: string): Promise<T | null> {
    try {
      const response = await fetch(`${endpoint}`, {
        method: "GET",
        headers: {
          ...(this.sharedHeaders as Record<string, string>)
        },
        credentials: 'include',
        signal: abortSignal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        return Promise.reject(error);
      }
      console.error("API GET request failed:", error);
      throw error;
    }
  }

  private async request<T>(url: string, options: RequestInit = {}): Promise<T> {
    // Get fresh token using the unified method
    const token = await getUnifiedAccessToken({
      scopes: ["api://418cddb7-3647-45c2-982e-fca3b0198b64/access_as_user"]
    });

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...options.headers as Record<string, string>,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    const response = await fetch(url, config);

    if (!response.ok) {
      // If we get 401, the token might be expired, try to refresh once
      if (response.status === 401) {

        // Clear any cached tokens and try to get a fresh one
        localStorage.removeItem("app_jwt");

        try {
          const freshToken = await getUnifiedAccessToken({
            scopes: ["api://418cddb7-3647-45c2-982e-fca3b0198b64/access_as_user"]
          });

          if (freshToken && freshToken !== token) {
            // Retry the request with the fresh token
            headers['Authorization'] = `Bearer ${freshToken}`;
            const retryResponse = await fetch(url, { ...config, headers });

            if (retryResponse.ok) {
              return retryResponse.json();
            }
          }
        } catch (refreshError) {

        }
      }

      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    return response.json();
  }

  // Salesforce authentication API
  async salesforceLogin(data: { userId: string; email?: string; taskId?: string }): Promise<{ token: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/api/auth/salesforce-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {

      throw error;
    }
  }

  // Subscription methods
  async createCheckoutSession(request: {
    email: string;
    name: string;
    successUrl: string;
    cancelUrl: string;
    priceId?: string;
    productId?: string;
    quantity?: number;
  }): Promise<{ checkoutUrl: string; sessionId: string }> {
    const response = await fetch(`${this.baseUrl}/api/admin/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create checkout session');
    }

    return await response.json();
  }

  async createSubscription(customerInfo: { email: string; name: string }): Promise<{ data: { clientSecret: string; subscriptionId: string } }> {
    const response = await fetch(`${this.baseUrl}/api/admin/create-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(customerInfo),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to create subscription');
    }

    return await response.json();
  }

  async checkSubscriptionStatus(): Promise<{ subscriptionVerified: boolean }> {
    const response = await fetch(`${this.baseUrl}/api/admin/check-subscription`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to check subscription status');
    }

    return await response.json();
  }

  async cancelSubscription(): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/admin/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to cancel subscription');
    }

    return await response.json();
  }

  // Organization & Seat Management APIs
  async getOrganizationDetails(): Promise<{
    type: 'personal' | 'enterprise';
    organization?: {
      id: string;
      name: string;
      email: string;
      subscriptionPlan: string;
      isActive: boolean;
    };
    seats?: {
      total: number;
      used: number;
      available: number;
    };
    members?: Array<{
      id: string;
      email: string;
      name: string;
      role: string;
      status: string;
      joinedAt: string;
    }>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/organization/details`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get organization details');
    }

    return await response.json();
  }

  async inviteOrganizationMember(email: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/organization/invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ email })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to invite member');
    }

    return await response.json();
  }

  async removeOrganizationMember(memberId: string): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/organization/member/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to remove member');
    }

    return await response.json();
  }

  async forfeitSeat(): Promise<{ message: string }> {
    const response = await fetch(`${this.baseUrl}/api/organization/forfeit-seat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to forfeit seat');
    }

    return await response.json();
  }

  async getPendingInvitations(): Promise<{
    invitations: Array<{
      invitationCode: string;
      organizationName: string;
      organizationEmail: string;
      invitedBy: string;
      invitedAt: string;
      expiresAt: string;
    }>;
  }> {
    const response = await fetch(`${this.baseUrl}/api/organization/pending-invitations`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to get pending invitations');
    }

    return await response.json();
  }

  async acceptInvitation(invitationCode: string): Promise<{
    message: string;
    member: {
      id: string;
      organizationId: string;
      email: string;
      name: string;
      role: string;
      status: string;
      joinedAt: string;
    };
  }> {
    const response = await fetch(`${this.baseUrl}/api/organization/accept-invitation`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({ invitationCode })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || errorData.error || 'Failed to accept invitation');
    }

    return await response.json();
  }

  // Conversation List and Sharing APIs
  async getConversationList(abortSignal: AbortSignal): Promise<{ ownedConversations: any[]; sharedConversations: any[] }> {
    return this.get<{ ownedConversations: any[]; sharedConversations: any[] }>('/api/conversations/list', abortSignal);
  }

  async shareConversation(request: { sessionId: string; emailAddresses: string[] }, abortSignal: AbortSignal): Promise<any> {
    return this.postJson<{ sessionId: string; emailAddresses: string[] }, any>('/api/conversation/share', request, abortSignal);
  }

  async unshareConversation(request: { sessionId: string; emailAddress: string }, abortSignal: AbortSignal): Promise<void> {
    return this.postJson<{ sessionId: string; emailAddress: string }, void>('/api/conversation/unshare', request, abortSignal);
  }

  async generateConversationTitle(request: { sessionId: string }, abortSignal: AbortSignal): Promise<{ title: string }> {
    return this.postJson<{ sessionId: string }, { title: string }>('/api/conversation/generate-title', request, abortSignal);
  }

  async updateConversationTitle(request: { sessionId: string; title: string }, abortSignal: AbortSignal): Promise<void> {
    return this.postJson<{ sessionId: string; title: string }, void>('/api/conversation/update-title', request, abortSignal);
  }
}
